import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatMoney, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SUCCESS } from "@/constants/testIds";
import { CheckCircle2, Ticket as TicketIcon, ArrowRight } from "lucide-react";

// Generate a simple QR code data URL via qrserver.com (free public API)
function qrUrl(code) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=4&data=${encodeURIComponent(code)}`;
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderQuery = params.get("order");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [event, setEvent] = useState(null);
  const [venue, setVenue] = useState(null);
  const [venueAddress, setVenueAddress] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const loadOrder = async (orderId) => {
      const { data } = await axios.get(`${API}/orders/${orderId}`);
      if (cancelled) return data.order.payment_status;
      setOrder(data.order);
      setTickets(data.tickets);
      setEvent(data.event);
      setVenue(data.venue);
      setVenueAddress(data.venue_address || null);
      setStatus(data.order.payment_status === "paid" ? "paid" : "pending");
      return data.order.payment_status;
    };

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        if (orderQuery) {
          const ps = await loadOrder(orderQuery);
          if (ps === "paid") return;
        } else if (sessionId) {
          const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (data.payment_status === "paid" && data.order_id) {
            await loadOrder(data.order_id);
            return;
          }
        }
      } catch (e) {
        console.error(e);
      }
      if (attempts < 15 && !cancelled) {
        setTimeout(poll, 1500);
      } else if (!cancelled) {
        setStatus("timeout");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderQuery]);

  const brand = venue?.brand_color || "#4338CA";

  if (status === "polling") {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full border-4 border-zinc-200 border-t-zinc-900 animate-spin" />
          <div className="mt-6 font-display font-bold text-xl">Confirming your payment…</div>
          <div className="text-sm text-zinc-500 mt-1">Hang tight, this takes a few seconds.</div>
        </div>
      </div>
    );
  }

  if (status === "timeout" || !order) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6">
        <div className="text-center max-w-md">
          <div className="font-display font-bold text-xl">Payment still processing</div>
          <p className="text-sm text-zinc-500 mt-2">
            Your bank confirmation is taking a moment. Check your email — your tickets will arrive there when the payment completes.
          </p>
          <Link to="/"><Button className="mt-6 rounded-full">Back to home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid={SUCCESS.root}>
      <header className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          {venue?.logo_url ? (
            <img src={resolveImageUrl(venue.logo_url)} alt={venue.name} className="h-9 w-9 object-contain rounded-md" />
          ) : (
            <div className="h-9 w-9 grid place-items-center rounded-md text-white font-display font-black text-sm" style={{ background: brand }}>
              {venue?.name?.[0] || "V"}
            </div>
          )}
          <div className="font-display font-black text-lg tracking-tight">{venue?.name}</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center">
          <div className="inline-flex w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="mt-5 font-display font-black text-4xl tracking-tighter">You&apos;re in!</h1>
          <p className="mt-2 text-zinc-600">Order <span className="font-mono" data-testid={SUCCESS.orderNumber}>{order.order_number}</span> · a confirmation email is on its way.</p>
        </div>

        {event && (
          <div className="mt-10 rounded-xl border border-zinc-200 p-6 bg-zinc-50">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Event</div>
            <div className="mt-1 font-display font-bold text-2xl">{event.title}</div>
            <div className="mt-1 text-sm text-zinc-600">
              {new Date(event.start_at).toLocaleString("en-GB", { dateStyle: "full", timeStyle: "short" })}
              {event.venue_location && ` · ${event.venue_location}`}
            </div>
            {venueAddress && (
              <div className="mt-4 pt-4 border-t border-zinc-200">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Venue address</div>
                <div className="mt-1 text-sm text-zinc-800 whitespace-pre-line">{venueAddress}</div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {tickets.map((t, i) => (
            <div key={t.id} className="rounded-xl border border-zinc-200 p-5 flex items-center gap-4" data-testid={SUCCESS.ticket(i)}>
              <img src={qrUrl(t.ticket_code)} alt="QR" className="w-24 h-24 rounded-md border border-zinc-200 bg-white" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 font-medium">
                  <TicketIcon className="w-3.5 h-3.5" /> Ticket
                </div>
                <div className="font-semibold">{t.tier_name}</div>
                <div className="mt-1 text-xs font-mono text-zinc-500 truncate">{t.ticket_code}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-zinc-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-zinc-600">Total paid</div>
            <div className="font-display font-bold text-2xl">{formatMoney(order.total_pence)}</div>
          </div>
        </div>

        <div className="mt-10 text-center">
          {venue && (
            <Link to={`/v/${venue.slug}`}>
              <Button variant="outline" className="rounded-full">
                Explore more {venue.name} events <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
